import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import {
  DEFAULT_SERVICE_TYPE_FORM_VALUES,
  type ServiceTypeFormFields,
  type ServiceTypeFormValues,
  serviceTypeFormSchema,
} from '../../../menus/category-form-schema';

export interface ServiceTypeFormProps {
  title: string;
  submitLabel: string;
  mode: 'create' | 'edit';
  defaultValues?: ServiceTypeFormFields;
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (values: ServiceTypeFormValues) => Promise<void>;
}

export function ServiceTypeForm({
  title,
  submitLabel,
  mode,
  defaultValues,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: ServiceTypeFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ServiceTypeFormFields, unknown, ServiceTypeFormValues>({
    resolver: zodResolver(serviceTypeFormSchema) as unknown as Resolver<
      ServiceTypeFormFields,
      unknown,
      ServiceTypeFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_SERVICE_TYPE_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  return (
    <Modal open onClose={onCancel} title={title} className="max-w-md">
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <Field
          label="Código"
          required
          disabled={mode === 'edit'}
          {...register('code')}
          {...maybeError(errors.code?.message)}
        />
        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />
        <Field
          label="Orden de presentación"
          type="number"
          min={0}
          {...register('displayOrder')}
          {...maybeError(errors.displayOrder?.message)}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
